import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import {
  styled,
  useTheme,
  useScrollTrigger,
  AppBar,
  Box,
  IconButton,
  Toolbar,
  Menu,
  MenuItem,
  Button,
  Avatar,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import MoreIcon from '@mui/icons-material/MoreVert';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '@/actions/session';
import { setSelectedProject } from '@/actions/projects';
import { useInitiateSignIn } from '@/hooks/useInitiateSignIn';
import {
  useAuth,
  useAuthDispatch,
  useUser,
  useUserDispatch,
} from '@/providers';
import { CLEAR_SESSION, LOGOUT, SET_USER, DELETE_USER } from '@/actions/types';
import AutoReload from './AutoReload';

const drawerWidth = 240;
const EMPTY_ARRAY = [];

function ElevationScroll(props) {
  const { children, window } = props;
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 15,
    target: window ? window() : undefined,
  });
  return React.cloneElement(children, {
    elevation: trigger ? 4 : 1,
  });
}

const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (prop) =>
    prop !== 'open' && prop !== 'pinned' && prop !== 'hasSidebar',
})(({ theme, open, pinned, hasSidebar }) => {
  const should_shift = hasSidebar && (open || pinned);
  return {
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    ...(should_shift && {
      marginLeft: drawerWidth,
      width: `calc(100% - ${drawerWidth}px)`,
      transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    }),
  };
});

function Header({
  open,
  toggleDrawer,
  pinned,
  colorInvert = false,
  window: windowProp,
}) {
  const dispatch = useDispatch(); // Still needed for setSelectedProject
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [projectAnchorEl, setProjectAnchorEl] = useState(null);
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = useState(null);

  const user = useUser();
  const session = useAuth();
  const authDispatch = useAuthDispatch();
  const userDispatch = useUserDispatch();
  const initiate_sign_in = useInitiateSignIn();
  const projects = useSelector((state) => state.projects ?? EMPTY_ARRAY);

  const theme = useTheme();
  const has_sidebar =
    session && session.isLoggedIn && user && user.status === 'approved';

  const isMenuOpen = Boolean(anchorEl);
  const isProjectMenuOpen = Boolean(projectAnchorEl);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProjectMenuOpen = (event) => {
    setProjectAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    handleMobileMenuClose();
  };

  const handleProjectMenuClose = () => {
    setProjectAnchorEl(null);
  };

  const handleMobileMenuOpen = (event) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };

  const handleLogout = async () => {
    // Clear context state first to prevent any components from trying to use stale session
    authDispatch({ type: CLEAR_SESSION });
    authDispatch({ type: LOGOUT });
    userDispatch({ type: DELETE_USER });
    handleMenuClose();
    // Then sign out from Amplify (this will clear Amplify's internal session)
    await logout();
    // Navigate to landing page after logout
    navigate('/', { replace: true });
  };

  const handleProjectSelect = async (projectId) => {
    try {
      const updatedUser = await dispatch(setSelectedProject(projectId, user));
      // Update context with the new user data
      if (updatedUser) {
        userDispatch({ type: SET_USER, payload: updatedUser });
      }
    } catch (error) {
      console.error('Error setting selected project:', error);
    }
    handleProjectMenuClose();
  };

  const logoLink = () => {
    if (session && session.isLoggedIn && user) {
      if (user.status === 'approved') {
        return '/dashboard';
      }
    }
    return '/';
  };

  const non_personal_projects = projects.filter(
    (project) => project.id !== user?.id,
  );
  const my_projects = non_personal_projects.filter((project) => project.owner);
  const other_projects = non_personal_projects.filter(
    (project) => !project.owner,
  );
  const selected_project = projects.find(
    (project) => project.id === user?.selected_project,
  );
  const has_project_options = my_projects.length > 0 || other_projects.length > 0;

  const menuId = 'primary-navbar-account-menu';
  const projectMenuId = 'primary-navbar-project-menu';
  const mobileMenuId = 'primary-navbar-account-menu-mobile';

  useEffect(() => {
    if (!session?.isLoggedIn || user?.status !== 'approved') {
      return;
    }
    if (!user?.selected_project) {
      return;
    }
    if (!Array.isArray(projects) || projects.length === 0) {
      return;
    }
    if (selected_project) {
      return;
    }

    // The stored selection points to a project that no longer exists locally.
    // Auto-heal by switching back to the Personal Project.
    handleProjectSelect(user.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    session?.isLoggedIn,
    user?.status,
    user?.id,
    user?.selected_project,
    projects,
    selected_project,
  ]);

  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      id={menuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem
        component={Link}
        to='/account/settings'
        onClick={handleMenuClose}
      >
        <IconButton size='large' color='inherit'>
          <PersonOutlineOutlinedIcon />
        </IconButton>
        <Typography>Account</Typography>
      </MenuItem>
      <MenuItem onClick={handleLogout}>
        <IconButton size='large' color='inherit'>
          <LogoutOutlinedIcon />
        </IconButton>
        <Typography>Logout</Typography>
      </MenuItem>
    </Menu>
  );

  const renderProjectMenu =
    session && session.isLoggedIn && user && user.status === 'approved' ? (
    <Menu
      anchorEl={projectAnchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      id={projectMenuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isProjectMenuOpen}
      onClose={handleProjectMenuClose}
    >
      <MenuItem
        onClick={() => handleProjectSelect(user.id)}
        disabled={selected_project?.id === user.id}
      >
        <Typography>Personal Project</Typography>
      </MenuItem>
      {my_projects.length > 0 && (
        <>
          <MenuItem disabled>
            <Typography variant='overline'>My Projects</Typography>
          </MenuItem>
          {my_projects.map((project) => (
            <MenuItem
              key={project.id}
              onClick={() => handleProjectSelect(project.id)}
              disabled={selected_project?.id === project.id}
            >
              <Typography>{project.name}</Typography>
            </MenuItem>
          ))}
        </>
      )}
      {other_projects.length > 0 && (
        <>
          <MenuItem disabled>
            <Typography variant='overline'>Shared Projects</Typography>
          </MenuItem>
          {other_projects.map((project) => (
            <MenuItem
              key={project.id}
              onClick={() => handleProjectSelect(project.id)}
              disabled={selected_project?.id === project.id}
            >
              <Typography>{project.name}</Typography>
            </MenuItem>
          ))}
        </>
      )}
    </Menu>
  ) : null;

  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
    >
      <MenuItem
        component='a'
        href={import.meta.env.VITE_DOC_URL}
        target='_blank'
      >
        <IconButton size='large' color='inherit'>
          <HelpOutlineOutlinedIcon />
        </IconButton>
        <Typography>Documentation</Typography>
      </MenuItem>
      {session && session.isLoggedIn && user && user.status === 'approved' && (
        <>
          <MenuItem
            component={Link}
            to='/account/settings'
            onClick={handleMobileMenuClose}
          >
            <IconButton size='large' color='inherit'>
              <PersonOutlineOutlinedIcon />
            </IconButton>
            <Typography>Account</Typography>
          </MenuItem>
          <MenuItem
            onClick={async () => {
              // Clear context state first to prevent any components from trying to use stale session
              authDispatch({ type: CLEAR_SESSION });
              authDispatch({ type: LOGOUT });
              userDispatch({ type: DELETE_USER });
              handleMobileMenuClose();
              // Then sign out from Amplify (this will clear Amplify's internal session)
              await logout();
              // Navigate to landing page after logout
              navigate('/', { replace: true });
            }}
          >
            <IconButton size='large' color='inherit'>
              <LogoutOutlinedIcon />
            </IconButton>
            <Typography>Logout</Typography>
          </MenuItem>
        </>
      )}
    </Menu>
  );

  return (
    <>
      <ElevationScroll window={windowProp}>
        <StyledAppBar
          position='sticky'
          sx={{
            top: 0,
            display: 'flex',
            backgroundColor: theme.palette.brandBlack.main,
            borderBottom: `1px solid ${theme.palette.brandBlack.main}`,
          }}
          open={open}
          pinned={pinned}
          hasSidebar={has_sidebar}
        >
          <Toolbar sx={{ backgroundColor: theme.palette.brandBlack.main }}>
            {has_sidebar && (
              <IconButton
                size='large'
                edge='start'
                onClick={toggleDrawer}
                aria-label='menu'
                sx={{
                  mr: 1,
                  color: theme.palette.brandBlack.contrastText,
                  ...(open && { display: 'none' }),
                }}
              >
                <MenuIcon fontSize='large' />
              </IconButton>
            )}
            {(!has_sidebar || !open) && (
              <>
                <Box
                  component={Link}
                  to={logoLink()}
                  title={import.meta.env.VITE_ORG_NAME}
                  sx={{
                    mr: 2,
                    display: { xs: 'none', md: 'flex' },
                    width: { xs: 100, md: 120 },
                  }}
                >
                  <Box
                    component='img'
                    src='/logo.png'
                    alt={import.meta.env.VITE_ORG_NAME}
                    sx={{
                      height: 'auto',
                      width: '100%',
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    mr: 2,
                    display: { xs: 'flex', md: 'none' },
                    flexGrow: 1,
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    component={Link}
                    to={logoLink()}
                    title={import.meta.env.VITE_ORG_NAME}
                    sx={{
                      width: { xs: 100, md: 120 },
                    }}
                  >
                    <Box
                      component='img'
                      src='/logo.png'
                      alt={import.meta.env.VITE_ORG_NAME}
                      sx={{
                        height: 'auto',
                        width: '100%',
                      }}
                    />
                  </Box>
                </Box>
              </>
            )}

            <Box sx={{ display: { xs: 'none', md: 'flex' }, flexGrow: 1 }} />
            {session &&
            session.isLoggedIn &&
            user &&
            user.status === 'approved' ? (
              <>
                <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                  {has_project_options && (
                      <Button
                        onClick={handleProjectMenuOpen}
                        endIcon={<KeyboardArrowDownIcon />}
                        sx={{
                          mr: 1,
                          textTransform: 'none',
                          color: theme.palette.brandBlack.contrastText,
                        }}
                      >
                        <Typography
                          sx={{ color: theme.palette.brandBlack.contrastText }}
                        >
                          Project:{' '}
                          {selected_project?.name ? selected_project.name : '(Select a project)'}
                        </Typography>
                      </Button>
                    )}
                  {user.status === 'approved' && !user.billing_account_id && (
                    <Button
                      component={Link}
                      to='/account/billing'
                      sx={{
                        mr: 1,
                        textTransform: 'none',
                        color: theme.palette.brandBlack.contrastText,
                      }}
                    >
                      <Typography
                        sx={{ color: theme.palette.brandBlack.contrastText }}
                      >
                        Credits: {user.credits?.toFixed(4) || '0.0000'}
                      </Typography>
                    </Button>
                  )}
                  <IconButton
                    size='large'
                    href={import.meta.env.VITE_DOC_URL}
                    target='_blank'
                    sx={{ color: theme.palette.brandBlack.contrastText }}
                  >
                    <HelpOutlineOutlinedIcon fontSize='large' />
                  </IconButton>
                  <IconButton
                    size='large'
                    edge='end'
                    aria-label='account of current user'
                    aria-controls={menuId}
                    aria-haspopup='true'
                    onClick={handleProfileMenuOpen}
                  >
                    <Avatar
                      alt={user?.given_name || user?.display_name}
                      src={user?.picture || '/bogus.jpg'}
                    />
                  </IconButton>
                </Box>
                <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
                  <IconButton
                    size='large'
                    aria-label='show more'
                    aria-controls={mobileMenuId}
                    aria-haspopup='true'
                    onClick={handleMobileMenuOpen}
                    sx={{ color: theme.palette.brandBlack.contrastText }}
                  >
                    <MoreIcon fontSize='large' />
                  </IconButton>
                </Box>
              </>
            ) : (
              <Button
                variant='contained'
                color='brand'
                size='large'
                onClick={async () => {
                  if (typeof window !== 'undefined') {
                    const location = new URL(window.location.href);
                    if (!['/', '/logout'].includes(location.pathname)) {
                      localStorage.setItem('redirect_path', location.pathname);
                      localStorage.setItem('redirect_search', location.search);
                    } else {
                      localStorage.removeItem('redirect_path');
                      localStorage.removeItem('redirect_search');
                    }
                    await initiate_sign_in();
                  }
                }}
              >
                Login/Signup
              </Button>
            )}
          </Toolbar>
        </StyledAppBar>
      </ElevationScroll>
      {renderMobileMenu}
      {renderMenu}
      {renderProjectMenu}
      <AutoReload
        tryDelay={parseInt(import.meta.env.VITE_AUTO_RELOAD_INTERVAL)}
      />
    </>
  );
}

export default Header;
